// ServiceProgramSection.js
// Displays the funeral/memorial service program for attendees
// Shows Order of Service, Pallbearers, Reception info, and Family Acknowledgment

import React, { useState, useRef } from 'react';

const ServiceProgramSection = ({ memorial }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const printRef = useRef(null);

  // Get service program data
  const serviceProgram = memorial?.serviceProgram;
  
  // Helper function to check if any meaningful service program content exists
  const hasServiceProgramData = () => {
    if (!serviceProgram) return false;
    
    const hasOrderOfService = serviceProgram.orderOfService && serviceProgram.orderOfService.length > 0;
    const hasActivePallbearers = serviceProgram.activePallbearers && serviceProgram.activePallbearers.length > 0;
    const hasHonoraryPallbearers = serviceProgram.honoraryPallbearers && serviceProgram.honoraryPallbearers.length > 0;
    const hasFlowerBearers = serviceProgram.flowerBearers && serviceProgram.flowerBearers.length > 0;
    const hasReceptionInfo = serviceProgram.receptionInfo && serviceProgram.receptionInfo.trim();
    const hasIntermentInfo = serviceProgram.intermentInfo && serviceProgram.intermentInfo.trim();
    const hasAcknowledgment = serviceProgram.familyAcknowledgment && serviceProgram.familyAcknowledgment.trim();
    
    return hasOrderOfService || hasActivePallbearers || hasHonoraryPallbearers || 
           hasFlowerBearers || hasReceptionInfo || hasIntermentInfo || hasAcknowledgment;
  };

  // Individual content checks for conditional rendering
  const hasOrderOfService = serviceProgram?.orderOfService && serviceProgram.orderOfService.length > 0;
  const hasActivePallbearers = serviceProgram?.activePallbearers && serviceProgram.activePallbearers.length > 0;
  const hasHonoraryPallbearers = serviceProgram?.honoraryPallbearers && serviceProgram.honoraryPallbearers.length > 0;
  const hasFlowerBearers = serviceProgram?.flowerBearers && serviceProgram.flowerBearers.length > 0;
  const hasReceptionInfo = serviceProgram?.receptionInfo && serviceProgram.receptionInfo.trim();
  const hasIntermentInfo = serviceProgram?.intermentInfo && serviceProgram.intermentInfo.trim();
  const hasAcknowledgment = serviceProgram?.familyAcknowledgment && serviceProgram.familyAcknowledgment.trim();

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    
    // Build Order of Service HTML
    let orderOfServiceHTML = '';
    if (hasOrderOfService) {
      const itemsHTML = serviceProgram.orderOfService.map((item, index) => `
        <div class="order-item">
          <div class="order-left">
            <span class="order-number">${index + 1}</span>
            <div class="order-content">
              <span class="order-title">${item.title || ''}</span>
              ${item.notes ? `<span class="order-notes">${item.notes}</span>` : ''}
            </div>
          </div>
          ${item.person ? `<span class="order-person">${item.person}</span>` : ''}
        </div>
      `).join('');
      
      orderOfServiceHTML = `
        <div class="section">
          <div class="section-title">
            <span class="diamond">◆</span>
            Order of Service
            <span class="diamond">◆</span>
          </div>
          <div class="order-list">
            ${itemsHTML}
          </div>
        </div>
      `;
    }

    // Build Pallbearers HTML
    let pallbearersHTML = '';
    if (hasActivePallbearers || hasHonoraryPallbearers) {
      let activeHTML = '';
      let honoraryHTML = '';
      
      if (hasActivePallbearers) {
        const names = serviceProgram.activePallbearers.map(name => 
          `<span class="name-tag">${name}</span>`
        ).join('');
        activeHTML = `
          <div class="pallbearer-group">
            <h4 class="group-title">Active</h4>
            <div class="names-grid">${names}</div>
          </div>
        `;
      }
      
      if (hasHonoraryPallbearers) {
        const names = serviceProgram.honoraryPallbearers.map(name => 
          `<span class="name-tag">${name}</span>`
        ).join('');
        honoraryHTML = `
          <div class="pallbearer-group">
            <h4 class="group-title">Honorary</h4>
            <div class="names-grid">${names}</div>
          </div>
        `;
      }
      
      pallbearersHTML = `
        <div class="section">
          <div class="section-title">
            <span class="diamond">◆</span>
            Pallbearers
            <span class="diamond">◆</span>
          </div>
          <div class="pallbearers-container">
            ${activeHTML}
            ${honoraryHTML}
          </div>
        </div>
      `;
    }

    // Build Flower Bearers HTML
    let flowerBearersHTML = '';
    if (hasFlowerBearers) {
      const names = serviceProgram.flowerBearers.map(name => 
        `<span class="name-tag">${name}</span>`
      ).join('');
      flowerBearersHTML = `
        <div class="section">
          <div class="section-title">
            <span class="diamond">◆</span>
            Flower Bearers
            <span class="diamond">◆</span>
          </div>
          <div class="names-grid centered">${names}</div>
        </div>
      `;
    }

    // Build After Service HTML
    let afterServiceHTML = '';
    if (hasIntermentInfo || hasReceptionInfo) {
      let intermentHTML = '';
      let receptionHTML = '';
      
      if (hasIntermentInfo) {
        intermentHTML = `
          <div class="info-box">
            <h4 class="info-title">Interment</h4>
            <p class="info-text">${serviceProgram.intermentInfo}</p>
          </div>
        `;
      }
      
      if (hasReceptionInfo) {
        receptionHTML = `
          <div class="info-box">
            <h4 class="info-title">Reception</h4>
            <p class="info-text">${serviceProgram.receptionInfo}</p>
          </div>
        `;
      }
      
      afterServiceHTML = `
        <div class="section">
          <div class="section-title">
            <span class="diamond">◆</span>
            After the Service
            <span class="diamond">◆</span>
          </div>
          <div class="after-service-grid">
            ${intermentHTML}
            ${receptionHTML}
          </div>
        </div>
      `;
    }

    // Build Acknowledgment HTML
    let acknowledgmentHTML = '';
    if (hasAcknowledgment) {
      acknowledgmentHTML = `
        <div class="section">
          <div class="section-title">
            <span class="heart">♥</span>
            Family Acknowledgment
            <span class="heart">♥</span>
          </div>
          <div class="acknowledgment">
            <span class="quote-mark open">"</span>
            <p>${serviceProgram.familyAcknowledgment}</p>
            <span class="quote-mark close">"</span>
          </div>
        </div>
      `;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Service Program - ${memorial.firstName} ${memorial.lastName}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,400&family=Montserrat:wght@300;400;500&display=swap');
            
            * { 
              margin: 0; 
              padding: 0; 
              box-sizing: border-box; 
            }
            
            @page {
              size: letter;
              margin: 0.5in;
            }
            
            body { 
              font-family: 'Cormorant Garamond', Georgia, serif; 
              color: #2c2c2c;
              background: #fff;
              line-height: 1.5;
              font-size: 14px;
            }
            
            .print-wrapper {
              max-width: 6.5in;
              margin: 0 auto;
              padding: 30px 40px;
              border: 6px double #c9a962;
              background: linear-gradient(135deg, #fdfcfa 0%, #f8f6f1 100%);
            }
            
            /* Header Styles */
            .header { 
              text-align: center; 
              margin-bottom: 30px;
              padding-bottom: 25px;
              border-bottom: 1px solid #e8e0d0;
            }
            
            .header .ornament {
              color: #c9a962;
              font-size: 18px;
              letter-spacing: 8px;
              margin-bottom: 12px;
            }
            
            .header .subtitle {
              font-family: 'Montserrat', sans-serif;
              font-size: 9px;
              letter-spacing: 4px;
              text-transform: uppercase;
              color: #888;
              margin-bottom: 10px;
            }
            
            .header h1 { 
              font-size: 28px; 
              font-weight: 400;
              letter-spacing: 2px;
              margin-bottom: 10px;
              color: #1a1a1a;
            }
            
            .header .dates {
              font-size: 14px;
              font-style: italic;
              color: #666;
            }
            
            /* Section Styles */
            .section { 
              margin-bottom: 25px;
              page-break-inside: avoid;
            }
            
            .section-title { 
              font-family: 'Montserrat', sans-serif;
              font-size: 10px;
              letter-spacing: 3px;
              text-transform: uppercase;
              font-weight: 500;
              margin-bottom: 15px;
              padding-bottom: 8px;
              border-bottom: 1px solid #e8e0d0;
              color: #666;
              text-align: center;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 10px;
            }
            
            .diamond, .heart {
              color: #c9a962;
              font-size: 10px;
            }
            
            /* Order of Service Styles */
            .order-list {
              background: #fff;
              border: 1px solid #e8e0d0;
              border-radius: 2px;
            }
            
            .order-item {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              padding: 10px 15px;
              border-bottom: 1px dotted #e8e0d0;
              page-break-inside: avoid;
            }
            
            .order-item:last-child { 
              border-bottom: none; 
            }
            
            .order-left {
              display: flex;
              align-items: flex-start;
              gap: 12px;
              flex: 1;
            }
            
            .order-number { 
              color: #fff;
              background: linear-gradient(135deg, #d4a84b 0%, #c9a962 100%);
              min-width: 24px;
              height: 24px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 12px;
              font-weight: 500;
              flex-shrink: 0;
            }
            
            .order-content {
              display: flex;
              flex-direction: column;
              gap: 2px;
            }
            
            .order-title { 
              font-weight: 500; 
              font-size: 14px;
              color: #333;
            }
            
            .order-notes { 
              color: #888; 
              font-size: 12px;
              font-style: italic;
            }
            
            .order-person { 
              color: #666; 
              font-style: italic; 
              font-size: 13px;
              text-align: right;
              flex-shrink: 0;
              margin-left: 15px;
            }
            
            /* Names Grid Styles */
            .pallbearers-container {
              display: flex;
              gap: 30px;
              justify-content: center;
              flex-wrap: wrap;
            }
            
            .pallbearer-group {
              text-align: center;
              min-width: 200px;
            }
            
            .group-title {
              font-family: 'Montserrat', sans-serif;
              font-size: 9px;
              letter-spacing: 2px;
              text-transform: uppercase;
              color: #996515;
              margin-bottom: 10px;
              font-weight: 500;
            }
            
            .names-grid {
              display: flex;
              flex-wrap: wrap;
              gap: 8px;
              justify-content: center;
            }
            
            .names-grid.centered {
              max-width: 400px;
              margin: 0 auto;
            }
            
            .name-tag {
              padding: 6px 14px;
              background: #f8f6f1;
              font-size: 13px;
              border: 1px solid #e8e0d0;
              border-radius: 2px;
            }
            
            /* After Service Styles */
            .after-service-grid {
              display: flex;
              gap: 20px;
              justify-content: center;
              flex-wrap: wrap;
            }
            
            .info-box {
              background: #f8f6f1;
              border: 1px solid #e8e0d0;
              padding: 15px 20px;
              border-radius: 2px;
              min-width: 200px;
              max-width: 280px;
              text-align: center;
            }
            
            .info-title {
              font-family: 'Montserrat', sans-serif;
              font-size: 9px;
              letter-spacing: 2px;
              text-transform: uppercase;
              color: #996515;
              margin-bottom: 8px;
              font-weight: 500;
            }
            
            .info-text {
              font-size: 13px;
              color: #555;
              line-height: 1.6;
            }
            
            /* Acknowledgment Styles */
            .acknowledgment {
              position: relative;
              font-style: italic;
              font-size: 14px;
              line-height: 1.8;
              padding: 20px 35px;
              background: #f8f6f1;
              border-left: 3px solid #c9a962;
              text-align: center;
            }
            
            .acknowledgment p {
              position: relative;
              z-index: 1;
            }
            
            .quote-mark {
              position: absolute;
              font-size: 40px;
              color: #c9a962;
              opacity: 0.3;
              font-family: Georgia, serif;
              line-height: 1;
            }
            
            .quote-mark.open {
              top: 10px;
              left: 10px;
            }
            
            .quote-mark.close {
              bottom: 0px;
              right: 10px;
            }
            
            /* Footer Styles */
            .footer {
              text-align: center;
              margin-top: 30px;
              padding-top: 20px;
              border-top: 1px solid #e8e0d0;
              color: #c9a962;
              font-size: 20px;
            }
            
            /* Print-specific adjustments */
            @media print {
              body { 
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              
              .print-wrapper { 
                border-width: 4px; 
                padding: 20px 30px; 
              }

              /* Small units must never be split down the middle… */
              .order-item,
              .bearer,
              .bearers-grid li,
              .after-item {
                page-break-inside: avoid;
                break-inside: avoid;
              }

              /* …but a whole SECTION must be allowed to flow across a page.
                 Forbidding that pushes a long order of service onto page two
                 in one lump and leaves page one half empty. */
              .section {
                page-break-inside: auto;
                break-inside: auto;
              }

              /* A heading never sits alone at the foot of a page. */
              .section-title,
              .section h2,
              .section h3 {
                page-break-after: avoid;
                break-after: avoid;
              }

              /* No single stranded line at a page edge. */
              p, li { orphans: 3; widows: 3; }

              .header { page-break-after: avoid; break-after: avoid; }
              .footer { page-break-before: avoid; break-before: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="print-wrapper">
            <div class="header">
              <div class="ornament">✦ ✦ ✦</div>
              <div class="subtitle">In Loving Memory</div>
              <h1>${memorial.firstName || ''} ${memorial.lastName || ''}</h1>
              <div class="dates">${formatDate(memorial.birthDate)} — ${formatDate(memorial.deathDate)}</div>
            </div>
            
            ${orderOfServiceHTML}
            ${pallbearersHTML}
            ${flowerBearersHTML}
            ${afterServiceHTML}
            ${acknowledgmentHTML}
            
            <div class="footer">✦</div>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();

    // Fit to a single sheet when the program is only slightly too tall.
    // Anything beyond ~1.7 pages is genuinely a two-page program and is left
    // to flow, guided by the page-break rules above.
    try {
      const fit = printWindow.document.createElement('script');
      fit.textContent = `
        (function () {
          var wrap = document.querySelector('.print-wrapper');
          if (!wrap) return;
          var pageHeight = 9.5 * 96;            // letter, less 0.5in margins
          var h = wrap.scrollHeight;
          if (h > pageHeight && h < pageHeight * 1.7) {
            var scale = Math.max(0.72, (pageHeight - 8) / h);
            wrap.style.zoom = scale;            // zoom reflows; transform does not
          }
        })();
      `;
      printWindow.document.body.appendChild(fit);
    } catch (e) {
      // Fitting is a nicety; printing must still work without it.
    }
    
    // Wait for fonts to load before printing
    setTimeout(() => {
      printWindow.print();
      // Don't auto-close so user can see preview
    }, 500);
  };

  return (
    <div className="mb-8 relative">
      {/* Main card — flat, like the other sections on the page */}
      <div className="relative">
        {/* Decorative Gold Border Frame */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            border: '1px solid #d4b896',
            background: 'transparent',
          }}
        ></div>
        <div 
          className="absolute pointer-events-none"
          style={{
            top: '8px',
            left: '8px',
            right: '8px',
            bottom: '8px',
            border: '1px solid #c9a962',
          }}
        ></div>
        
        {/* Inner Content Area */}
        <div 
          className="relative p-6 md:p-10 lg:p-12"
          style={{
            background: 'linear-gradient(145deg, #fdfcfa 0%, #f9f7f3 50%, #f5f2eb 100%)',
          }}
        >
          {/* Corner Ornaments */}
          <div className="absolute top-4 left-4 text-amber-600/40 text-2xl">❧</div>
          <div className="absolute top-4 right-4 text-amber-600/40 text-2xl rotate-90">❧</div>
          <div className="absolute bottom-4 left-4 text-amber-600/40 text-2xl -rotate-90">❧</div>
          <div className="absolute bottom-4 right-4 text-amber-600/40 text-2xl rotate-180">❧</div>
          
          {/* Header */}
          <div className="text-center mb-8 pt-4">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full group"
            >
              {/* Top Ornament */}
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="h-px w-12 md:w-20 bg-gradient-to-r from-transparent to-amber-400/50"></div>
                <span className="text-amber-500 text-sm tracking-[0.5em]">✦ ✦ ✦</span>
                <div className="h-px w-12 md:w-20 bg-gradient-to-l from-transparent to-amber-400/50"></div>
              </div>
              
              <p className="text-xs tracking-[0.3em] text-gray-400 uppercase mb-2">Order of Service</p>
              <h2 className="text-2xl md:text-3xl font-extralight text-gray-900 tracking-wide" style={{ letterSpacing: '0.04em' }}>
                Service Program
              </h2>
              
              {/* Expand/Collapse Indicator */}
              <div className="mt-5 flex justify-center">
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${isExpanded ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'} border group-hover:bg-amber-50 group-hover:border-amber-400`}
                  style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                >
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </button>
          </div>

          {isExpanded && (
            <div className="relative">
              {hasServiceProgramData() ? (
                <>
                  {/* Print Button */}
                  <div className="flex justify-center mb-8">
                    <button
                      onClick={handlePrint}
                      className="group flex items-center gap-3 px-8 py-3 border-2 border-amber-600 text-amber-700 text-xs uppercase tracking-[0.2em] hover:bg-amber-600 hover:text-white transition-all duration-300"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      Print Program
                    </button>
                  </div>

                  {/* Printable Content - Screen Display */}
                  <div ref={printRef} className="space-y-10 max-w-3xl mx-auto">
                
                    {/* Order of Service */}
                    {hasOrderOfService && (
                      <div className="section">
                        {/* Section Card */}
                        <div 
                          className="rounded-sm overflow-hidden"
                          style={{
                            boxShadow: '0 4px 20px -4px rgba(0,0,0,0.1), 0 2px 8px -2px rgba(0,0,0,0.06)',
                            background: 'white',
                            border: '1px solid #ebe6dc',
                          }}
                        >
                          {/* Section Header */}
                      <div 
                        className="px-6 py-4 text-center"
                        style={{
                          background: 'linear-gradient(135deg, #f8f5ef 0%, #f3efe6 100%)',
                          borderBottom: '1px solid #ebe6dc',
                        }}
                      >
                        <div className="flex items-center justify-center gap-3">
                          <span className="text-amber-500 text-xs">◆</span>
                          <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-gray-600">
                            Order of Service
                          </h3>
                          <span className="text-amber-500 text-xs">◆</span>
                        </div>
                      </div>
                      
                      {/* Items List */}
                      <div className="divide-y divide-gray-100">
                        {serviceProgram.orderOfService.map((item, index) => (
                          <div 
                            key={index} 
                            className="flex flex-col sm:flex-row sm:items-center justify-between px-6 py-4 hover:bg-amber-50/30 transition-colors duration-200"
                          >
                            <div className="flex items-start gap-4 flex-1">
                              <span 
                                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0"
                                style={{
                                  background: 'linear-gradient(135deg, #f6e5c3 0%, #e8d4a8 100%)',
                                  color: '#8b6914',
                                  border: '1px solid #dcc48c',
                                }}
                              >
                                {index + 1}
                              </span>
                              <div className="flex-1 min-w-0 pt-1">
                                <span className="text-gray-800 font-medium text-base block">{item.title}</span>
                                {item.notes && (
                                  <span className="text-gray-500 text-sm mt-0.5 block italic">{item.notes}</span>
                                )}
                              </div>
                            </div>
                            {item.person && (
                              <span className="text-gray-500 text-sm mt-2 sm:mt-0 ml-12 sm:ml-4 sm:text-right italic">
                                {item.person}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Pallbearers */}
                {(hasActivePallbearers || hasHonoraryPallbearers) && (
                  <div className="section">
                    <div 
                      className="rounded-sm overflow-hidden"
                      style={{
                        boxShadow: '0 4px 20px -4px rgba(0,0,0,0.1), 0 2px 8px -2px rgba(0,0,0,0.06)',
                        background: 'white',
                        border: '1px solid #ebe6dc',
                      }}
                    >
                      {/* Section Header */}
                      <div 
                        className="px-6 py-4 text-center"
                        style={{
                          background: 'linear-gradient(135deg, #f8f5ef 0%, #f3efe6 100%)',
                          borderBottom: '1px solid #ebe6dc',
                        }}
                      >
                        <div className="flex items-center justify-center gap-3">
                          <span className="text-amber-500 text-xs">◆</span>
                          <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-gray-600">
                            Pallbearers
                          </h3>
                          <span className="text-amber-500 text-xs">◆</span>
                        </div>
                      </div>
                      
                      <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {hasActivePallbearers && (
                            <div>
                              <h4 className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-3 font-medium text-center">
                                Active
                              </h4>
                              <div className="flex flex-wrap justify-center gap-2">
                                {serviceProgram.activePallbearers.map((name, index) => (
                                  <span 
                                    key={index}
                                    className="px-4 py-2 text-gray-700 text-sm rounded-sm"
                                    style={{
                                      background: 'linear-gradient(135deg, #faf8f4 0%, #f5f1e8 100%)',
                                      border: '1px solid #e8e0d0',
                                    }}
                                  >
                                    {name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {hasHonoraryPallbearers && (
                            <div>
                              <h4 className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-3 font-medium text-center">
                                Honorary
                              </h4>
                              <div className="flex flex-wrap justify-center gap-2">
                                {serviceProgram.honoraryPallbearers.map((name, index) => (
                                  <span 
                                    key={index}
                                    className="px-4 py-2 text-gray-700 text-sm rounded-sm"
                                    style={{
                                      background: 'linear-gradient(135deg, #faf8f4 0%, #f5f1e8 100%)',
                                      border: '1px solid #e8e0d0',
                                    }}
                                  >
                                    {name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Flower Bearers */}
                {hasFlowerBearers && (
                  <div className="section">
                    <div 
                      className="rounded-sm overflow-hidden"
                      style={{
                        boxShadow: '0 4px 20px -4px rgba(0,0,0,0.1), 0 2px 8px -2px rgba(0,0,0,0.06)',
                        background: 'white',
                        border: '1px solid #ebe6dc',
                      }}
                    >
                      {/* Section Header */}
                      <div 
                        className="px-6 py-4 text-center"
                        style={{
                          background: 'linear-gradient(135deg, #f8f5ef 0%, #f3efe6 100%)',
                          borderBottom: '1px solid #ebe6dc',
                        }}
                      >
                        <div className="flex items-center justify-center gap-3">
                          <span className="text-amber-500 text-xs">◆</span>
                          <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-gray-600">
                            Flower Bearers
                          </h3>
                          <span className="text-amber-500 text-xs">◆</span>
                        </div>
                      </div>
                      
                      <div className="p-6">
                        <div className="flex flex-wrap justify-center gap-2">
                          {serviceProgram.flowerBearers.map((name, index) => (
                            <span 
                              key={index}
                              className="px-4 py-2 text-gray-700 text-sm rounded-sm"
                              style={{
                                background: 'linear-gradient(135deg, #faf8f4 0%, #f5f1e8 100%)',
                                border: '1px solid #e8e0d0',
                              }}
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* After Service - Interment & Reception */}
                {(hasIntermentInfo || hasReceptionInfo) && (
                  <div className="section">
                    <div 
                      className="rounded-sm overflow-hidden"
                      style={{
                        boxShadow: '0 4px 20px -4px rgba(0,0,0,0.1), 0 2px 8px -2px rgba(0,0,0,0.06)',
                        background: 'white',
                        border: '1px solid #ebe6dc',
                      }}
                    >
                      {/* Section Header */}
                      <div 
                        className="px-6 py-4 text-center"
                        style={{
                          background: 'linear-gradient(135deg, #f8f5ef 0%, #f3efe6 100%)',
                          borderBottom: '1px solid #ebe6dc',
                        }}
                      >
                        <div className="flex items-center justify-center gap-3">
                          <span className="text-amber-500 text-xs">◆</span>
                          <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-gray-600">
                            After the Service
                          </h3>
                          <span className="text-amber-500 text-xs">◆</span>
                        </div>
                      </div>
                      
                      <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {hasIntermentInfo && (
                            <div 
                              className="p-5 rounded-sm"
                              style={{
                                background: 'linear-gradient(135deg, #fafaf8 0%, #f5f4f0 100%)',
                                border: '1px solid #eae6dc',
                              }}
                            >
                              <div className="flex items-start gap-4">
                                <div 
                                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{
                                    background: 'linear-gradient(135deg, #f0ebe0 0%, #e8e2d4 100%)',
                                    border: '1px solid #ddd6c4',
                                  }}
                                >
                                  <svg className="w-5 h-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                </div>
                                <div>
                                  <h4 className="text-xs uppercase tracking-[0.15em] text-amber-700 mb-1.5 font-semibold">Interment</h4>
                                  <p className="text-gray-700 text-sm leading-relaxed">{serviceProgram.intermentInfo}</p>
                                </div>
                              </div>
                            </div>
                          )}
                          {hasReceptionInfo && (
                            <div 
                              className="p-5 rounded-sm"
                              style={{
                                background: 'linear-gradient(135deg, #fafaf8 0%, #f5f4f0 100%)',
                                border: '1px solid #eae6dc',
                              }}
                            >
                              <div className="flex items-start gap-4">
                                <div 
                                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{
                                    background: 'linear-gradient(135deg, #f0ebe0 0%, #e8e2d4 100%)',
                                    border: '1px solid #ddd6c4',
                                  }}
                                >
                                  <svg className="w-5 h-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                  </svg>
                                </div>
                                <div>
                                  <h4 className="text-xs uppercase tracking-[0.15em] text-amber-700 mb-1.5 font-semibold">Reception</h4>
                                  <p className="text-gray-700 text-sm leading-relaxed">{serviceProgram.receptionInfo}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Family Acknowledgment */}
                {hasAcknowledgment && (
                  <div className="section">
                    <div 
                      className="rounded-sm overflow-hidden"
                      style={{
                        boxShadow: '0 4px 20px -4px rgba(0,0,0,0.1), 0 2px 8px -2px rgba(0,0,0,0.06)',
                        background: 'white',
                        border: '1px solid #ebe6dc',
                      }}
                    >
                      {/* Section Header */}
                      <div 
                        className="px-6 py-4 text-center"
                        style={{
                          background: 'linear-gradient(135deg, #f8f5ef 0%, #f3efe6 100%)',
                          borderBottom: '1px solid #ebe6dc',
                        }}
                      >
                        <div className="flex items-center justify-center gap-3">
                          <span className="text-amber-500 text-xs">♥</span>
                          <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-gray-600">
                            Family Acknowledgment
                          </h3>
                          <span className="text-amber-500 text-xs">♥</span>
                        </div>
                      </div>
                      
                      <div className="p-6 md:p-8">
                        <div className="relative">
                          {/* Decorative Quotes */}
                          <span 
                            className="absolute -top-2 -left-1 text-5xl leading-none opacity-20"
                            style={{ color: '#c9a962', fontFamily: 'Georgia, serif' }}
                          >"</span>
                          <span 
                            className="absolute -bottom-4 -right-1 text-5xl leading-none opacity-20 rotate-180"
                            style={{ color: '#c9a962', fontFamily: 'Georgia, serif' }}
                          >"</span>
                          
                          <p 
                            className="text-gray-600 italic leading-loose text-base md:text-lg whitespace-pre-wrap px-6 py-2 relative z-10"
                            style={{ fontFamily: 'Georgia, serif' }}
                          >
                            {serviceProgram.familyAcknowledgment}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Footer Ornament */}
              <div className="flex items-center justify-center gap-4 mt-10 pt-4">
                <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-300/50"></div>
                <span className="text-amber-400 text-lg">✝</span>
                <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-300/50"></div>
              </div>
                </>
              ) : (
                /* Empty State - No Service Program Data */
                <div className="text-center py-12 px-6">
                  <div className="max-w-md mx-auto">
                    <div className="text-4xl mb-6 text-amber-400/50">❧</div>
                    <p 
                      className="text-gray-500 font-light text-lg leading-relaxed"
                      style={{ fontFamily: 'Georgia, serif' }}
                    >
                      We sincerely thank you for attending the memorial service for our loved one.
                    </p>
                    <div className="flex items-center justify-center gap-3 mt-6">
                      <div className="h-px w-8 bg-amber-300/40"></div>
                      <span className="text-amber-400/60 text-sm">✦</span>
                      <div className="h-px w-8 bg-amber-300/40"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceProgramSection;
